import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, FormField } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { CourierSelect } from "./CourierSelect";
import { PackageUpload } from "./PackageUpload";
import { BulkRateResults } from "./BulkRateResults";
import { toast } from "sonner";
import { Calculator, MapPin, Package } from "lucide-react";

const VOLUMETRIC_DIVISOR = 5000;
const MIN_DEAD_WEIGHT = 0.5;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const B2CRateCalculator = ({ user, onLogout }) => {
  const [pickupLocations, setPickupLocations] = useState([]);
  const [pickupMode, setPickupMode] = useState("location"); // 'location' | 'pincode'
  const [pickupLocationId, setPickupLocationId] = useState("");
  const [pickupPincode, setPickupPincode] = useState("");
  const [dropPincode, setDropPincode] = useState("");
  const [paymentMode, setPaymentMode] = useState("prepaid");
  const [declaredValue, setDeclaredValue] = useState("");
  const [pkg, setPkg] = useState({ dead_weight_kg: "0.5", length_cm: "", breadth_cm: "", height_cm: "" });
  const [pkgMode, setPkgMode] = useState("single"); // 'single' | 'upload'
  const [upload, setUpload] = useState(null); // parsed rows from PackageUpload (each row = own lane)

  const [quoteInput, setQuoteInput] = useState(null); // set on submit -> drives CourierSelect
  const [bulkInput, setBulkInput] = useState(null); // set on submit in upload mode -> drives BulkRateResults

  const switchMode = (mode) => {
    setPkgMode(mode);
    setQuoteInput(null);
    setBulkInput(null);
  };

  useEffect(() => {
    axios
      .get(`${API}/pickup-locations/`)
      .then((r) => setPickupLocations(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const volumetric = useMemo(() => {
    const l = parseFloat(pkg.length_cm) || 0;
    const b = parseFloat(pkg.breadth_cm) || 0;
    const h = parseFloat(pkg.height_cm) || 0;
    return round2((l * b * h) / VOLUMETRIC_DIVISOR);
  }, [pkg]);

  const applicable = useMemo(
    () => round2(Math.max(parseFloat(pkg.dead_weight_kg) || 0, volumetric, MIN_DEAD_WEIGHT)),
    [pkg, volumetric]
  );

  // Reactive validation — same rules drive the toast on submit AND the button's
  // enabled state, so "Calculate rates" only lights up once every required field is valid.
  const validationError = useMemo(() => {
    // Upload mode: pincodes + package come from every row of the file.
    if (pkgMode === "upload") return upload ? null : "Upload a file";
    if (pickupMode === "location" && !pickupLocationId) return "Select a pickup location";
    if (pickupMode === "pincode" && !/^\d{6}$/.test(pickupPincode)) return "Enter a valid 6-digit pickup pincode";
    if (!/^\d{6}$/.test(dropPincode)) return "Enter a valid 6-digit delivery pincode";
    if ((parseFloat(pkg.dead_weight_kg) || 0) < MIN_DEAD_WEIGHT) return `Dead weight must be at least ${MIN_DEAD_WEIGHT} kg`;
    if (!(parseFloat(pkg.length_cm) > 0 && parseFloat(pkg.breadth_cm) > 0 && parseFloat(pkg.height_cm) > 0))
      return "Enter package length, breadth and height";
    return null;
  }, [pickupMode, pickupLocationId, pickupPincode, dropPincode, pkg, pkgMode, upload]);

  const calculate = (e) => {
    e?.preventDefault?.();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (pkgMode === "upload") {
      // Every row is rated on its own lane; payment mode + declared value apply to all rows.
      setBulkInput({
        payment_mode: paymentMode,
        order_value: parseFloat(declaredValue) || 0,
        rows: upload.rows.map(({ row, pickup_pincode, drop_pincode, dead_weight_kg, length_cm, breadth_cm, height_cm }) => ({
          row, pickup_pincode, drop_pincode, dead_weight_kg, length_cm, breadth_cm, height_cm,
        })),
      });
      return;
    }
    setQuoteInput({
      ...(pickupMode === "location"
        ? { pickup_location_id: pickupLocationId }
        : { pickup_pincode: pickupPincode }),
      drop_pincode: dropPincode,
      payment_mode: paymentMode,
      order_value: parseFloat(declaredValue) || 0,
      package: {
        dead_weight_kg: parseFloat(pkg.dead_weight_kg),
        length_cm: parseFloat(pkg.length_cm),
        breadth_cm: parseFloat(pkg.breadth_cm),
        height_cm: parseFloat(pkg.height_cm),
      },
    });
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <PageHeader
          title="Rate Calculator"
          subtitle="Compare courier rates for any lane — no order is created."
        />

        <form onSubmit={calculate} className="space-y-6">
          <div className="flex gap-2" role="group" aria-label="Calculation mode">
            <Button type="button" size="sm" variant={pkgMode === "single" ? "default" : "outline"}
              onClick={() => switchMode("single")} data-testid="rc-pkg-single">
              Single shipment
            </Button>
            <Button type="button" size="sm" variant={pkgMode === "upload" ? "default" : "outline"}
              onClick={() => switchMode("upload")} data-testid="rc-pkg-upload">
              Bulk upload
            </Button>
          </div>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-brand" /> Lane
              </CardTitle>
              <CardDescription>
                {pkgMode === "upload"
                  ? "Pickup and delivery pincodes come from each row of your file. Payment mode and declared value apply to every row."
                  : "Pickup and delivery pincodes determine the zone."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {pkgMode === "single" && (
                <>
                  {/* Pickup mode toggle */}
                  <div className="flex gap-2" role="group" aria-label="Pickup source">
                    <Button type="button" size="sm" variant={pickupMode === "location" ? "default" : "outline"}
                      onClick={() => setPickupMode("location")} data-testid="rc-pickup-location">
                      Saved location
                    </Button>
                    <Button type="button" size="sm" variant={pickupMode === "pincode" ? "default" : "outline"}
                      onClick={() => setPickupMode("pincode")} data-testid="rc-pickup-pincode">
                      Pincode
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {pickupMode === "location" ? (
                      <FormField label="Pickup location" htmlFor="rc-pickup-loc" required>
                        <SearchableSelect
                          value={pickupLocationId}
                          onValueChange={setPickupLocationId}
                          placeholder="Select pickup location"
                          emptyText="No pickup locations found."
                          options={pickupLocations.map((loc) => ({
                            value: loc.id,
                            label: `${loc.facility_name || loc.company_name || "Pickup"} - ${loc.city || ""}`,
                            searchLabel: `${loc.facility_name || ""} ${loc.city || ""} ${loc.pincode || ""}`,
                          }))}
                        />
                      </FormField>
                    ) : (
                      <FormField label="Pickup pincode" htmlFor="rc-pickup-pin" required>
                        <Input id="rc-pickup-pin" data-testid="rc-pickup-pin" inputMode="numeric" maxLength={6}
                          value={pickupPincode}
                          onChange={(e) => setPickupPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                      </FormField>
                    )}
                    <FormField label="Delivery pincode" htmlFor="rc-drop-pin" required>
                      <Input id="rc-drop-pin" data-testid="rc-drop-pin" inputMode="numeric" maxLength={6}
                        value={dropPincode}
                        onChange={(e) => setDropPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                    </FormField>
                  </div>
                </>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Payment mode" htmlFor="rc-payment">
                  <div className="flex gap-2">
                    {["prepaid", "cod"].map((m) => (
                      <Button key={m} type="button" size="sm" variant={paymentMode === m ? "default" : "outline"}
                        onClick={() => setPaymentMode(m)} data-testid={`rc-payment-${m}`} className="capitalize">
                        {m === "cod" ? "COD" : "Prepaid"}
                      </Button>
                    ))}
                  </div>
                </FormField>
                <FormField label="Declared value (₹)" htmlFor="rc-declared" hint="Used for COD fee % calculation.">
                  <Input id="rc-declared" data-testid="rc-declared" type="number" step="0.01" min="0" inputMode="decimal"
                    value={declaredValue} onChange={(e) => setDeclaredValue(e.target.value)} />
                </FormField>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-brand" /> Package
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {pkgMode === "upload" ? (
                <PackageUpload value={upload} onChange={setUpload} />
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <FormField label="Dead weight (kg)" htmlFor="rc-dead" required hint={`Min ${MIN_DEAD_WEIGHT} kg`}>
                      <Input id="rc-dead" data-testid="rc-dead-weight" type="number" step="0.01" min={MIN_DEAD_WEIGHT} inputMode="decimal"
                        value={pkg.dead_weight_kg} onChange={(e) => setPkg({ ...pkg, dead_weight_kg: e.target.value })} />
                    </FormField>
                    <FormField label="Length (cm)" htmlFor="rc-l" required>
                      <Input id="rc-l" data-testid="rc-length" type="number" step="0.1" min="0" inputMode="decimal"
                        value={pkg.length_cm} onChange={(e) => setPkg({ ...pkg, length_cm: e.target.value })} />
                    </FormField>
                    <FormField label="Breadth (cm)" htmlFor="rc-b" required>
                      <Input id="rc-b" data-testid="rc-breadth" type="number" step="0.1" min="0" inputMode="decimal"
                        value={pkg.breadth_cm} onChange={(e) => setPkg({ ...pkg, breadth_cm: e.target.value })} />
                    </FormField>
                    <FormField label="Height (cm)" htmlFor="rc-h" required>
                      <Input id="rc-h" data-testid="rc-height" type="number" step="0.1" min="0" inputMode="decimal"
                        value={pkg.height_cm} onChange={(e) => setPkg({ ...pkg, height_cm: e.target.value })} />
                    </FormField>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="text-slate-500">Volumetric: <span className="font-semibold tabular-nums text-slate-700">{volumetric} kg</span></span>
                    <span className="text-brand font-medium">Applicable weight: <span className="font-bold tabular-nums" data-testid="rc-applicable">{applicable} kg</span></span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-col items-end gap-1.5">
            {validationError && (
              <span className="text-xs text-slate-500" data-testid="rc-validation-hint">
                {validationError}
              </span>
            )}
            <Button
              type="submit"
              data-testid="rc-calculate"
              disabled={!!validationError}
              title={validationError || "Calculate rates"}
            >
              <Calculator className="w-4 h-4 mr-2" /> Calculate rates
            </Button>
          </div>
        </form>

        {/* Read-only courier options */}
        {(quoteInput || bulkInput) && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Courier rates</CardTitle>
              <CardDescription>Estimated landed costs. No order is created from this screen.</CardDescription>
            </CardHeader>
            <CardContent>
              {bulkInput ? <BulkRateResults input={bulkInput} /> : <CourierSelect quoteInput={quoteInput} readOnly />}
            </CardContent>
          </Card>
        )}
      </div>
    </SellerLayout>
  );
};

export default B2CRateCalculator;
